import { Button, Stack, Typography } from '@mui/material';
import { useApolloClient } from '@apollo/client';
import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { styled } from '@mui/material/styles';
import { ME_QUERY } from '~/graphql/queries/me';
import { useToast } from '~/shared/contexts/ToastContext';

/**
 * Stripe Checkout `cancel_url` when the attorney leaves without paying.
 * Refetch on mount: the app navigates away to Stripe before mutations can run follow-up refetches.
 */
const RetainerFundingCancelPage = () => {
  const apolloClient = useApolloClient();
  const { showToast } = useToast();

  useEffect(() => {
    let cancelled = false;

    async function syncAfterFundingCancel() {
      try {
        await apolloClient.refetchQueries({
          include: 'active',
        });
        await apolloClient.query({
          query: ME_QUERY,
          fetchPolicy: 'network-only',
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        Sentry.captureException(error, {
          tags: { feature: 'retainer_funding_cancel_sync' },
        });
        showToast(
          'Could not refresh your data. Open your matter to see the latest retainer status.',
          'error',
        );
      }
    }

    void syncAfterFundingCancel();

    return () => {
      cancelled = true;
    };
  }, [apolloClient, showToast]);

  return (
    <Root spacing={2}>
      <PageTitle variant="h6">Checkout cancelled</PageTitle>
      <Body variant="body2" color="text.secondary">
        No charge was made. You can return to your matter and start funding
        again when you are ready.
      </Body>
      <Button
        component={RouterLink}
        to="/matters"
        variant="contained"
        fullWidth
      >
        Back to matters
      </Button>
    </Root>
  );
};

const Root = styled(Stack)(({ theme }) => ({
  maxWidth: 480,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
}));

const Body = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export default RetainerFundingCancelPage;
